import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import Slideover from 'components/Common/slideover';
import SettingToggle from 'components/Common/settingToggle';
import { TextArea, Segmented, Options, Option, Tooltip } from '@payscale/design';
import { CycleDateSection } from 'nomst/Components/CycleSettings/cycleDateSection';
import { CycleActionButtons } from 'nomst/Components/CycleSettings/cycleActionButtons';
import { DeleteCycleButton } from 'nomst/Components/CycleSettings/deleteCycleButton';
import { EmployeeCard } from 'nomst/Components/Decision/employeeCard';
import { EmployeeCycleInfo } from 'nomst/Components/Decision/EmployeeCardDetails/employeeCycleInfo';
import { CycleConfigMenu } from 'nomst/Components/Configuration/cycleConfigMenu';
import {
    EmployeeClass, CompaRatioDisplayFormat, BaseOfCalculation, PayRangeDisplayType, ConfigViews,
    CycleStatus, AmountCode, Roles, FeatureFlags
} from 'typings/enums';
import { Range } from 'nomst/Stores/Decision/range';
import { IEmployee } from 'typings/employee';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { Employee } from 'nomst/Stores/Decision/employee';
import { IAmount } from 'typings/amount';
import { AdvancedCycleSettings } from './advancedCycleSettings';
import { User } from 'nomst/Stores/User/user';
import * as actions from 'nomst/Actions/configurations';
import './cycleSettingsView.scss';
import { configurationsStore, cycleRules } from 'utils/MobxDataManager/singletons';
import { EmailManagementHeader } from 'nomst/Components/CycleSettings/emailManagementHeader';
import { EmailManagementContainer } from 'nomst/Components/CycleSettings/emailManagementContainer';
import { PreviewCycleAlert } from 'nomst/Components/Decision/previewCycleAlert';
import { UnpublishedCycleAlert } from 'nomst/Components/CycleSettings/unpublishedCycleAlert';
import { featureFlagHelper } from 'singletons';

export interface ICycleSettingsViewProps {
    store: MobxTeamStore;
    className?: string;
}

export interface ICycleSettingsViewState {
    isSlidOver: boolean;
    comment: string;
    showSavedAlert: boolean;
}

const compaRatioString = 'Compa-Ratio';
const rangePenetrationString = 'Range Penetration';

export class CycleSettingsView extends React.Component<ICycleSettingsViewProps, ICycleSettingsViewState> {
    private readonly meritRecommendation: number = 1850;
    private readonly plannedBonus: number =  5000;
    private readonly plannedVariable: number = 1000;

    constructor(props: ICycleSettingsViewProps) {
        super(props);
        this.handleSlideoverClick = this.handleSlideoverClick.bind(this);
        this.determineIsSlid = this.determineIsSlid.bind(this);
        this.handleSetLockViewForManager = this.handleSetLockViewForManager.bind(this);
        this.handleClickChangeComment = this.handleClickChangeComment.bind(this);
        this.handlePerformanceRatingToggle = this.handlePerformanceRatingToggle.bind(this);
        this.handlePerformanceRatingSelect = this.handlePerformanceRatingSelect.bind(this);
        this.handlePayRangeToggle = this.handlePayRangeToggle.bind(this);
        this.handleRangePenerationDisplayTypeChange = this.handleRangePenerationDisplayTypeChange.bind(this);
        this.handleCompaRatioDisplayFormatChange = this.handleCompaRatioDisplayFormatChange.bind(this);
        this.handleBaseOfCalculationChange = this.handleBaseOfCalculationChange.bind(this);
        this.handleTccSettingsToggle = this.handleTccSettingsToggle.bind(this);
        this.handleTccFieldsChange = this.handleTccFieldsChange.bind(this);
        this.handleCompSummaryToggle = this.handleCompSummaryToggle.bind(this);
        this.handleHideIneligibleEmployeesToggle = this.handleHideIneligibleEmployeesToggle.bind(this);
        this.handleCompSummaryFieldsChange = this.handleCompSummaryFieldsChange.bind(this);
        this.handleSaveComment = this.handleSaveComment.bind(this);
        this.handleDeleteCycleClick = this.handleDeleteCycleClick.bind(this);
        this.handleDisableCompMemoDonwload = this.handleDisableCompMemoDonwload.bind(this);
        const initialComment = configurationsStore.convertedCycleSettingsViewCycle.comment || '';
        this.state = {
            isSlidOver: false,
            comment: initialComment,
            showSavedAlert: false
        };
    }

    render(): React.ReactNode {
        return (
            <div className="nomst-cycle-settings">
                <UnpublishedCycleAlert />
                <PreviewCycleAlert />
                <Slideover
                    isSlid={this.determineIsSlid}
                    panelCenter={this.renderPanelCenter()}
                    panelLeft={this.renderPanelLeft()}
                    panelRight={this.renderPanelRight()}
                />
            </div>
        );
    }

    private renderPanelCenter(): React.ReactNode {
        if (!configurationsStore.convertedCycleSettingsViewCycle.cycleId) {
            return null;
        }

        const isDisabled = this.isDisabled();
        const { user } = this.props.store;
        const displayEmployee = this.getDisplayEmployee();
        const isLockedForManager = configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.lockViewForManager;
        // manager user for employee card preview
        // handles read only view and hiding the eligibility and proration override link
        const mockUser = new User('previewUser', 'User Name', '', [Roles.Manager], false);
        // tslint:disable-next-line: max-line-length
        const tooltipText = 'Everyone will see this employee information included in their team view regardless of level or role.';
        const hiddenAlertClass = this.state.showSavedAlert ? '' : 'nomst-cycle-settings__saved-alert--hidden';
        const disableCompMemoDownload = configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.disableCompMemoDownload;

        return (
            <div className="nomst-cycle-settings__panel-center">
                <div className="nomst-cycle-settings__header">
                    <h1 className="nomst-cycle-settings__title">
                        Cycle Settings
                    </h1>
                    <div className={`nomst-cycle-settings__saved-alert ${hiddenAlertClass}`}>
                        <i className="icon-check nomst-cycle-settings__saved-check" />
                        <span>Cycle changes saved</span>
                    </div>
                    <CycleActionButtons
                        isDisabled={isDisabled}
                        configuration={configurationsStore.convertedCycleSettingsViewCycle}
                        configurationsStore={configurationsStore}
                        isHrAdmin={user.isHrAdmin}
                    />
                </div>
                <CycleDateSection
                    isDisabled={isDisabled}
                    configuration={configurationsStore.convertedCycleSettingsViewCycle}
                />
                <div className="nomst-cycle-settings__toggle-download">
                    <label htmlFor="compensation-download" className="pxl-segmented-label">Disable Compensation Memo Download</label>
                    <Segmented
                        className="pxl-segmented--secondary"
                        value={
                            disableCompMemoDownload
                        }
                        id="compensation-download"
                        onChange={this.handleDisableCompMemoDonwload}
                        options={[{ text: 'On', value: true }, { text: 'Off', value: false }]}
                        disabled={false}
                    />
                </div>
                <div className="nomst-cycle-settings__team-view">
                    <div className="nomst-cycle-settings__team-view-header">
                        <h3 className="nomst-cycle-settings__team-view-heading">
                            Employee Preview
                            <Tooltip
                                hoverText={tooltipText}
                                tooltipText=""
                                display="icon"
                                className="nomst-cycle-settings__header-tooltip"
                                id="cycle-settings-header-tooltip"
                            />
                        </h3>
                        {!cycleRules.isCycleClosedForId(configurationsStore.cycleSettingsId) && (
                            <button
                                className="nomst-cycle-settings__team-view-edit-link pxl-link"
                                onClick={this.handleSlideoverClick}
                            >
                                <i className="icon-cog" />
                                Edit View
                            </button>
                        )}
                    </div>
                    <div className="nomst-cycle-settings__employee-card">
                        <EmployeeCard
                            key={1}
                            decisionDispatcher={null}
                            countryDecisionDispatcher={null}
                            employee={displayEmployee}
                            configuration={configurationsStore.convertedCycleSettingsViewCycle}
                            isCycleLocked={isLockedForManager}
                            selectedEmployeeId={'2'}
                        />
                    </div>
                    <div className="nomst-cycle-settings__employee-cycle-info">
                        <EmployeeCycleInfo
                            configuration={configurationsStore.convertedCycleSettingsViewCycle}
                            countryDecisionDispatcher={null}
                            employee={displayEmployee}
                            user={mockUser}
                            isCycleLocked={isLockedForManager}
                            showMultiComponentBonus={false}
                        />
                    </div>
                    <div className="nomst-cycle-settings__team-view-edit">
                        <div className="nomst-cycle-settings__team-view-read-control">
                            <label htmlFor="lock-team-view-segmented" className="pxl-segmented-label">Read-only view</label>
                            <Segmented
                                className="pxl-segmented--secondary"
                                value={
                                    configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.lockViewForManager
                                }
                                id="lock-team-view-segmented"
                                onChange={this.handleSetLockViewForManager}
                                options={[{ text: 'On', value: true }, { text: 'Off', value: false }]}
                                disabled={cycleRules.isCycleClosedForId(configurationsStore.convertedCycleSettingsViewCycle.cycleId)}
                            />
                        </div>
                    </div>
                </div>
                {this.renderEmailNotifications()}
                <div>
                    <EmailManagementHeader
                        isDisabled={isDisabled}
                    />
                    <EmailManagementContainer
                        isDisabled={isDisabled}
                    />
                </div>

                {configurationsStore.convertedCycleSettingsViewCycle.isCurrentlyPending && (
                    <DeleteCycleButton
                        onDeleteCycleClick={this.handleDeleteCycleClick}
                    />
                )}
            </div>
        );
    }

    private renderPanelLeft(): React.ReactNode {
        if (!configurationsStore.cycleConfigurations) {
            return null;
        }
        return (
            <div className="nomst-cycle-settings__panel-left">
                <CycleConfigMenu
                    cycleConfigs={configurationsStore.convertedConfigurations}
                    selectedCycleId={configurationsStore.convertedCycleSettingsViewCycle.cycleId}
                    currentView={ConfigViews.Cycle}
                    configurationsStore={configurationsStore}
                />
            </div>
        );
    }

    private renderPanelRight(): React.ReactNode {
        const selectedCycleConfig = configurationsStore.cycleSettingsViewCycle;
        if (!selectedCycleConfig || !configurationsStore.convertedCycleSettingsViewCycle.cycleId) {
            return null;
        }
        const showPeformance =
            configurationsStore.convertedCycleSettingsViewCycle.ratingConfiguration ?
                configurationsStore.convertedCycleSettingsViewCycle.ratingConfiguration.showPerformanceRating :
                false;
        const showInternalRange =
            configurationsStore.convertedCycleSettingsViewCycle.payRangeConfiguration.showPayRange;
        const showTcc = configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.tcc;
        const showIncreaseRecommendation = selectedCycleConfig.teamPageViews.increaseRecommendation;
        const managerIncreaseDecision = selectedCycleConfig.teamPageViews.managerIncreaseDecision;
        const managerIncreasePercentageUnder =
            selectedCycleConfig.teamPageViews.managerIncreasePercentageUnder;
        const managerIncreasePercentageOver =
            selectedCycleConfig.teamPageViews.managerIncreasePercentageOver;
        const budgetFields = selectedCycleConfig.budgetFields;
        const preFillDecision = selectedCycleConfig.teamPageViews.preFillDecision;
        const preFillDecisionFields = configurationsStore.convertedCycleSettingsViewCycle ?
            configurationsStore.convertedCycleSettingsViewCycle.teamPageViews.preFillDecisionFields : [];
        const useCompSummaryFields = selectedCycleConfig.teamPageViews.shouldDisplayAllCompSummaryFields;
        const cycleStatus = selectedCycleConfig.status ? selectedCycleConfig.status : CycleStatus.Pending;
        const hideIneligibleEmployees = selectedCycleConfig.teamPageViews.hideIneligibleEmployees;

        return (
            <div className="nomst-cycle-settings__panel-right">
                <h2 className="nomst-cycle-settings__heading">Customize Team View</h2>
                <p className="nomst-cycle-settings__subheading">
                    Select what your managers see when making pay decisions
                </p>
                <button className="nomst-cycle-settings__close no-btn-style" onClick={this.handleSlideoverClick}>
                    <i className="icon-cancel" />
                </button>
                <SettingToggle
                    body={this.renderPerformanceRating()}
                    className="setting-performance-rating"
                    settingId="performanceRating"
                    settingName="Performance Rating"
                    initialValue={showPeformance}
                    onChange={this.handlePerformanceRatingToggle}
                />
                <SettingToggle
                    body={this.renderInternalPayRange()}
                    className="setting-internal-pay-range"
                    settingId="internalPayRange"
                    settingName="Internal Pay Range"
                    initialValue={showInternalRange}
                    onChange={this.handlePayRangeToggle}
                />
                <SettingToggle
                    body={this.renderTccFieldSettings()}
                    className="setting-tcc-fields"
                    settingId="tccFields"
                    settingName="Total Cash Compensation (TCC)"
                    initialValue={showTcc}
                    onChange={this.handleTccSettingsToggle}
                />
                <SettingToggle
                    body={this.renderCompSummaryFields()}
                    className="setting-comp-summary-fields"
                    settingId="compSummaryFields"
                    settingName="Comp Summary Fields"
                    initialValue={useCompSummaryFields}
                    onChange={this.handleCompSummaryToggle}
                />
                {featureFlagHelper.featureFlagSettings[FeatureFlags.ShowHideIneligibleEmployees] && 
                    <SettingToggle
                        body={this.renderHideIneligibleEmployees()}
                        className="setting-hide-ineligible-employees"
                        settingId="hideIneligibleEmployees"
                        settingName="Hide Ineligible Employees"
                        initialValue={hideIneligibleEmployees}
                        onChange={this.handleHideIneligibleEmployeesToggle}
                    />
                }
                <AdvancedCycleSettings
                    cycleStatus={cycleStatus}
                    budgetFields={budgetFields}
                    preFillDecision={preFillDecision}
                    preFillDecisionFields={preFillDecisionFields}
                    showIncreaseRecommendation={showIncreaseRecommendation}
                    managerIncreaseDecision={managerIncreaseDecision}
                    managerIncreasePercentageUnder={managerIncreasePercentageUnder}
                    managerIncreasePercentageOver={managerIncreasePercentageOver}
                />
            </div>
        );
    }

    private renderEmailNotifications(): React.ReactNode {
        const isDisabled = this.isDisabled();
        return (
            <div className="email-notifications">
                <h3 className="email-notifications__heading">Email Notifications</h3>
                <p className="email-notifications__explanation">
                    Message to managers will be sent once cycle is opened. This message can help communicate your
                    organization&apos;s compensation strategy and help managers with their pay decisions.
                </p>
                <TextArea
                    rows={4}
                    cols={20}
                    id="message-to-managers"
                    value={this.state.comment}
                    onChange={this.handleClickChangeComment}
                    disabled={isDisabled}
                />
                <button
                    className="email-notifications__save-btn pxl-btn pxl-btn--primary"
                    onClick={this.handleSaveComment}
                    disabled={isDisabled}
                >
                    Save Email Notification
                </button>
            </div>
        );
    }

    private renderCompSummaryFields(): React.ReactNode {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        return (
            <>
                <p className="setting-comp-summary-fields__header">
                    Select which fields are displayed in the Comp Summary.
                    Excluded fields will be included in the total calculation but hidden from view.
                </p>
                <Options
                    id="compFields"
                    options={configuration.compSummaryFieldOptions}
                    onChange={this.handleCompSummaryFieldsChange}
                    horizontal={true}
                    syncOptions={false}
                    value={configuration.selectedCompSummaryFields}
                    type="pills"
                    optionProps={{'className': 'pxl-pill--secondary'}}
                />
            </>
        );
    }

    private renderTccFieldSettings(): React.ReactNode {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        return (
            <>
                <p className="setting-tcc-fields__header">
                    Select fields to include in the TCC calculation.
                    Fields not included will be displayed outside of the TCC calculation.
                </p>
                <Options
                    id="tccFieldOptions"
                    options={configuration.possibleBudgetFieldsForTccCalc}
                    onChange={this.handleTccFieldsChange}
                    syncOptions={false}
                    value={configuration.allFieldNamesThatAffectTcc}
                    horizontal={true}
                    type="pills"
                    optionProps={{'className': `pxl-pill--secondary ${!configuration.teamPageViews.tcc ? 'disabled' : ''}`}}
                />
            </>
        );
    }

    private renderPerformanceRating(): React.ReactNode {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        if (!configuration.ratingConfiguration) {
            return null;
        }
        return (
            <Option
                checked={configuration.ratingConfiguration.allowEditPerformanceRating}
                className="pxl-option--secondary"
                onChange={this.handlePerformanceRatingSelect}
                text="Manager can select performance rating"
                type="checklist"
            />
        );
    }

    private renderInternalPayRange(): React.ReactNode {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        const className = configuration.payRangeConfiguration.displayType === PayRangeDisplayType.RangePenetration ?
            'disabled' : '';
        return (
            <div className="internal-pay-range">
                <div className="internal-pay-range__label-options">
                    <label htmlFor="internal-pay-range-options" className="radio-block-label">Show as</label>
                    <Options
                        id="internal-pay-range-options"
                        type="radio"
                        multiValue={false}
                        options={[rangePenetrationString, compaRatioString]}
                        value={this.convertDisplayTypeToString(configuration.payRangeConfiguration.displayType)}
                        onChange={this.handleRangePenerationDisplayTypeChange}
                        syncOptions={false}
                        optionProps={{'className': 'pxl-option--primary'}}
                    />
                </div>
                <div className="compa-ratio-options">
                    <div className={`compa-ratio-options__select ${className}`}>
                        <label htmlFor="compa-ratio-options" className="radio-block-label">Show as</label>
                        <Options
                            id="compa-ratio-options"
                            type="radio"
                            multiValue={false}
                            options={[
                                CompaRatioDisplayFormat.ShowAsPercentage,
                                CompaRatioDisplayFormat.ShowAsDecimal]}
                            value={configuration.payRangeConfiguration.compaRatioDisplayFormat}
                            onChange={this.handleCompaRatioDisplayFormatChange}
                            syncOptions={false}
                            optionProps={{'className': 'pxl-option--primary'}}
                        />
                    </div>
                </div>
                <div className="pay-range-calc">
                    <h3 className="pay-range-calc__header">Determine calculation of employee pay versus range</h3>
                    <div className="pay-range-calc__options">
                        <label htmlFor="pay-range-calculation-options" className="radio-block-label">Show as</label>
                        <Options
                            id="pay-range-calculation-options"
                            type="radio"
                            multiValue={false}
                            options={[BaseOfCalculation.BasePay, BaseOfCalculation.TCC]}
                            value={configuration.payRangeConfiguration.baseOfCalculation}
                            onChange={this.handleBaseOfCalculationChange}
                            syncOptions={false}
                            optionProps={{'className': 'pxl-option--primary'}}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderHideIneligibleEmployees(): React.ReactNode {
        return (
            <p className="setting-hide-ineligible-employees__header">
                Employees marked ineligible for all budgets will not be displayed.
                Managers will only see eligible employees when making decisions.
            </p>
        );
    }

    private isDisabled(): boolean {
        return this.state.isSlidOver ||
            cycleRules.isCycleClosedForId(configurationsStore.convertedCycleSettingsViewCycle.cycleId);
    }

    private handleTccSettingsToggle(id: string, value: boolean): Promise<void>  {
        return actions.toggleShowTccFields(value);
    }

    private handleDisableCompMemoDonwload(id: string, value: boolean): Promise<void>  {
        return actions.toggleDisableCompMemoDownload(value);
    }

    private handleTccFieldsChange(id: string, value: string[]): Promise<void>  {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        const removing = configuration.allFieldNamesThatAffectTcc.length > value.length;
        return actions.addRemoveFieldsForTccCalc(value, removing);
    }

    private handleSetLockViewForManager(_: undefined, value: boolean): Promise<void> {
        this.setState({ showSavedAlert: true });
        return actions.toggleLockViewForManagers(value);
    }

    private handleClickChangeComment(_: undefined, value: string): void {
        this.setState({comment: value});
    }

    private handleSaveComment(): Promise<void>  {
        this.setState({ showSavedAlert: true });
        return actions.setConfigurationComment(this.state.comment);
    }

    private convertDisplayTypeToString(displayType: PayRangeDisplayType): string {
        if (displayType === PayRangeDisplayType.CompaRatio) {
            return compaRatioString;
        }
        return rangePenetrationString;
    }

    private determineIsSlid(): boolean {
        return this.state.isSlidOver;
    }

    private handleSlideoverClick(): void {
        const showSlider = this.determineIsSlid();
        this.setState({
            isSlidOver: !showSlider,
            showSavedAlert: showSlider
        });
    }

    private handlePerformanceRatingToggle(id: string, value: boolean): Promise<void>  {
        return actions.toggleShowPerformanceRating(value);
    }

    private handlePayRangeToggle(id: string, value: boolean): Promise<void>  {
        return actions.toggleShowPayRange(value);
    }

    private handleBaseOfCalculationChange(id: string, value: string): Promise<void>  {
        return actions.setBaseOfCalculation(value as BaseOfCalculation);
    }

    private handleCompaRatioDisplayFormatChange(id: string, value: string): Promise<void>  {
        return actions.setCompRatioDisplayFormat(value as CompaRatioDisplayFormat);
    }

    private handleRangePenerationDisplayTypeChange(id: string, value: string): Promise<void>  {
        const payRangeType = (value === compaRatioString) ?
            PayRangeDisplayType.CompaRatio : PayRangeDisplayType.RangePenetration;
        return actions.setPayRangeDisplayType(payRangeType);
    }

    private async handlePerformanceRatingSelect(): Promise<void>  {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        if (!configuration.ratingConfiguration) {
            return;
        }
        const value = !configuration.ratingConfiguration.allowEditPerformanceRating;
        return actions.toggleEditPerformanceRating(value);
    }

    private handleCompSummaryToggle(id: string, value: boolean): Promise<void>  {
        return actions.toggleUseCompSummaryFields(value);
    }

    private handleHideIneligibleEmployeesToggle(_: string, value: boolean): Promise<void> {
        return actions.toggleHideIneligibleEmployees(value);
    }

    private handleCompSummaryFieldsChange(_: undefined, values: string[]): Promise<void>  {
        return actions.setExcludedCompSummaryFields(values);
    }

    private async handleDeleteCycleClick(): Promise<void> {
        const selectedCycleConfig = configurationsStore.cycleSettingsViewCycle;
        if (selectedCycleConfig) {
            await configurationsStore.deleteCycleConfiguration(selectedCycleConfig);
        }
    }

    private getDisplayEmployee(): Employee {
        const configuration = configurationsStore.convertedCycleSettingsViewCycle;
        const amounts = [] as IAmount[];
        if (!configuration) {
            return {} as Employee;
        }

        // Figure out which budgets the user has created and create
        // amounts the employee can display for them
        if (configuration.cycleBudgetFields) {
            configuration.cycleBudgetFields.forEach(budget => {
                let value = 0;
                if (configuration.teamPageViews.preFillDecision
                    && configuration.teamPageViews.preFillDecisionFields.indexOf(budget.amountCode) > -1) {
                    value = this.getPreFilledValue(budget.amountCode);
                }
                amounts.push(
                    {
                        amountCycleId: budget.amountCycleId,
                        amountCode: budget.amountCode,
                        isEligible: true,
                        title: budget.title,
                        amountIsoCurrencyCode: 'USD',
                        value
                    } as IAmount
                );
            });
        }

        const performanceRatingsList = configuration.ratingConfiguration ?
            configuration.ratingConfiguration.performanceRatings[0] : null;
        const performanceRating = performanceRatingsList ? performanceRatingsList.name : '';

        const employeeDto = {
            country: 'US',
            employeeSourceId: 'Never gonna give you up, never gonna let you down...',
            amounts,
            employeeClass: EmployeeClass.Annual,
            payEffectiveDate: new Date(),
            displayName: 'Employee Name',
            firstName: 'Employee',
            lastName: 'Name',
            middleName: '',
            jobTitleInternal: 'Job Title',
            jobCodeKey: '',
            hasPromotionRequest: false,
            promotionId: '',
            isoCurrencyCode: 'USD',
            fullTimeEquivalent: 40,
            meritRecommendation: this.meritRecommendation,
            plannedBonus: this.plannedBonus,
            plannedVariable: this.plannedVariable,
            currentPay: 60700,
            annualizedCurrentPay: 60700,
            performanceRating,
            commissionsPaid: 4000,
            totalAnnualShortTermIncentives: 1000,
            reviewStatus: true, // TODO: PSP-22423 Replace with actual flag
            bonus: 0,
            range: new Range({minRange: 52000, midRange: 62500, maxRange: 73000} as IRange,
                'USD', false, new CurrencyConversion(configuration.currencies)),
            payHistory: null
        } as IEmployee;

        return new Employee(
            employeeDto, 40, null, configuration, null, null);
    }

    private getPreFilledValue(amountCode: AmountCode): number {
        switch (amountCode) {
            case AmountCode.Merit:
                return this.meritRecommendation;
            case AmountCode.Bonus:
                return this.plannedBonus;
            case AmountCode.VariablePay:
                return this.plannedVariable;
            default:
                return 0;
        }
    }
}

export default inject('store')(observer(CycleSettingsView));